/**
 * PlayHQ helpers
 * ==============
 *
 * Small pure functions used by both the members and admin PlayHQ views
 * to derive display data from cached PlayHQ documents.
 *
 * The cached documents keep the raw PlayHQ payload under `raw`, so most
 * of the useful information (team logos, grade info, per-innings stats)
 * lives there rather than at the top level of the flattened doc.
 */

/**
 * Returns a compact "10:30 AM, Sat 1 Nov 2025" string for a UTC ISO date,
 * falling back to the raw input if parsing fails. Matches PlayHQ's own
 * kickoff row layout.
 */
export function formatKickoff(iso) {
  if (!iso) return "TBC";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * Pick a small square logo (~48px) from a PlayHQ image ladder. PlayHQ
 * returns multiple sizes under `logo.sizes[]`; we want a compact avatar
 * for list rows.
 */
export function pickLogo(node, targetSize = 48) {
  const sizes = node?.logo?.sizes || node?.club?.logo?.sizes || [];
  if (!Array.isArray(sizes) || sizes.length === 0) return null;
  let best = sizes[0];
  let bestDelta = Math.abs((sizes[0]?.dimensions?.width || 0) - targetSize);
  for (const s of sizes) {
    const w = s?.dimensions?.width || 0;
    const delta = Math.abs(w - targetSize);
    if (delta < bestDelta) {
      best = s;
      bestDelta = delta;
    }
  }
  return best?.url || null;
}

/**
 * Given a cached game doc, compute a friendly per-team score summary
 * out of the raw `periods` array.
 *
 * Returns a map: `{ [teamId]: [{runs, wickets, overs, status}, …] }`
 * where each element is one innings for that team (ordered as they appear
 * in `periods`). Non-batting innings are ignored.
 */
export function scoresByTeam(gameRaw) {
  const out = {};
  const periods = gameRaw?.periods || [];
  for (const p of periods) {
    for (const t of p.teams || []) {
      if (t.discipline !== "BATTING") continue;
      const stats = (t.outcome?.statistics || []).reduce((acc, s) => {
        acc[s.type] = s.value;
        return acc;
      }, {});
      if (!out[t.id]) out[t.id] = [];
      out[t.id].push({
        runs: stats.TOTAL_SCORE,
        wickets: stats.TOTAL_OUTS,
        overs: stats.TOTAL_OVERS,
        status: t.outcome?.status || null,
      });
    }
  }
  return out;
}

/**
 * Format a single innings PlayHQ-style: "10/199 (50)" — wickets first, then
 * runs, then overs in parens. Matches the layout PlayHQ uses on their
 * public game centre. Returns "—" when the innings didn't happen.
 */
export function formatInnings(inn) {
  if (!inn || inn.runs == null) return "—";
  const w = inn.wickets != null ? inn.wickets : "";
  const overs = inn.overs != null ? ` (${inn.overs})` : "";
  return `${w}${w !== "" ? "/" : ""}${inn.runs}${overs}`;
}

/**
 * Format `game.type` — a camelCased enum ("oneDay", "twoDay", "t20", …) —
 * into a friendly label ("One Day", "Two Day", "T20"). Falls back to the
 * raw value when we don't know it.
 */
export function formatGameType(t) {
  if (!t) return null;
  const map = {
    oneDay: "One Day",
    twoDay: "Two Day",
    threeDay: "Three Day",
    fourDay: "Four Day",
    fiveDay: "Five Day",
    t20: "T20",
    t10: "T10",
    limited: "Limited overs",
    twenty20: "T20",
  };
  return map[t] || t.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

/**
 * Combine a game's cached `venue` block into the PlayHQ-style
 * "Surface / Venue" string (e.g. "North Warrane Oval / North Warrane Oval").
 * When the surface and venue are identical, still repeats — this mirrors
 * PlayHQ's own presentation on their game centre.
 */
export function formatVenue(venue) {
  if (!venue) return null;
  const surface = venue.surface_name;
  const parent = venue.venue_name;
  if (surface && parent) return `${surface} / ${parent}`;
  return surface || parent || null;
}

/**
 * Given a list of cached team docs, return only those belonging to the
 * configured club (matching `raw.club.id === orgId`).
 */
export function filterClubTeams(teams, orgId) {
  if (!Array.isArray(teams) || !orgId) return [];
  return teams.filter((t) => (t.raw?.club?.id || "") === orgId);
}

/**
 * Build a `{ id -> team }` lookup from a list of cached team docs, so
 * fixture rows can display team names without another round-trip.
 */
export function teamsById(teams) {
  const map = {};
  for (const t of teams || []) {
    if (t.playhq_id) map[t.playhq_id] = t;
  }
  return map;
}

/**
 * Group cached fixture rows by `round_name`, preserving the order of first
 * appearance (which matches the PlayHQ round order).
 */
export function groupByRound(fixtures) {
  const order = [];
  const map = new Map();
  for (const f of fixtures || []) {
    const key = f.round_name || "Round";
    if (!map.has(key)) {
      order.push(key);
      map.set(key, []);
    }
    map.get(key).push(f);
  }
  return order.map((name) => ({ name, games: map.get(name) }));
}


// ===========================================================================
// Scorecard helpers — derive display-ready structures from a PlayHQ game
// summary payload (`data` returned by `/api/playhq/games/{id}/summary`).
// ===========================================================================

/** Turn a statistics array `[{type, value}]` into a `{type: value}` map. */
function statsMap(arr) {
  const m = {};
  for (const s of arr || []) m[s.type] = s.value;
  return m;
}

/** Get a friendly display name for a player appearance. */
export function appearanceName(app) {
  if (!app) return "Unknown";
  const first = app.firstName || "";
  const last = (app.lastName || "").toUpperCase();
  return [first, last].filter(Boolean).join(" ") || "Unknown";
}

/**
 * Build a lookup of `appearanceId -> appearance` for the game so per-innings
 * player refs (which use appearance ids, not user ids) can be resolved.
 */
export function appearanceIndex(game) {
  const out = {};
  for (const a of (game && game.appearances) || []) {
    if (a?.id) out[a.id] = a;
  }
  return out;
}

/**
 * Format a dismissal for display — e.g. "c: Ricky Cowen · b: Brayden Drake"
 * or "run out: Callum Stocks" or "b: Brayden Drake" or "not out".
 * Reads from the innings' shared statistics graph.
 */
export function formatDismissal(batterAppId, sharedStats, appIndex) {
  const dismissals = (sharedStats || []).filter((s) =>
    (s.appearances || []).some((a) => a.role === "BATTING" && a.id === batterAppId)
  );
  if (dismissals.length === 0) return "not out";

  // Combine multi-role dismissals into a single label per type.
  const d = dismissals[0];
  const roles = (d.appearances || []).reduce((acc, a) => {
    acc[a.role] = a.id; return acc;
  }, {});
  const nameOf = (id) => appearanceName(appIndex[id]);
  switch (d.type) {
    case "CAUGHT":
      return `c: ${nameOf(roles.FIELDING)}   b: ${nameOf(roles.BOWLING)}`;
    case "BOWLED":
      return `b: ${nameOf(roles.BOWLING)}`;
    case "LBW":
      return `lbw: ${nameOf(roles.BOWLING)}`;
    case "STUMPED":
      return `st: ${nameOf(roles.FIELDING)}   b: ${nameOf(roles.BOWLING)}`;
    case "HIT_WICKET":
      return `hit wicket: ${nameOf(roles.BOWLING)}`;
    case "RUN_OUT": {
      const fielders = [roles.FIELDING, roles.ASSISTANT_FIELDING]
        .filter(Boolean).map(nameOf).join(" / ");
      return `run out: ${fielders}`;
    }
    default:
      return d.type.toLowerCase().replace(/_/g, " ");
  }
}

/**
 * Derive a display-ready innings from a period entry.
 * Returns `{battingTeamId, bowlingTeamId, batters[], bowlers[], totals, fallOfWickets[], dismissals[], name}`.
 *
 * When ``registryMap`` (an object keyed by lower-cased "first last" name)
 * is provided, each row's ``playhqId`` and ``name`` are pulled from the
 * canonical player registry rather than the per-game appearance record.
 * This keeps scorecards, leaderboards, and career lookups all pointing
 * at the same admin-maintained mapping.
 */
export function deriveInnings(period, appIndex, registryMap = null) {
  const teams = period?.teams || [];
  const bat = teams.find((t) => t.discipline === "BATTING");
  const bowl = teams.find((t) => t.discipline === "BOWLING");
  if (!bat || !bowl) return null;

  const shared = period.sharedStatistics || [];

  // Helper: resolve (playhqId, displayName) for a given appearance id.
  const resolvePlayer = (appId) => {
    const app = appIndex[appId];
    const fallbackId = app?.playerId || app?.id || appId || "";
    const rawFirst = (app?.firstName || "").trim();
    const rawLast = (app?.lastName || "").trim();
    const nameKey = `${rawFirst} ${rawLast}`.trim().toLowerCase();
    const reg = registryMap && nameKey ? registryMap[nameKey] : null;
    return {
      playhqId: (reg && reg.playhq_id) || fallbackId,
      displayName: (reg && reg.display_name) || appearanceName(app),
    };
  };

  const batters = (bat.appearances || [])
    .slice()
    .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999))
    .map((b) => {
      const s = statsMap(b.statistics);
      const p = resolvePlayer(b.id);
      return {
        appearanceId: b.id,
        playhqId: p.playhqId,
        name: p.displayName,
        captain: appIndex[b.id]?.captainRole,
        status: b.status,           // "OUT" | "NOT_OUT" | null (didn't bat)
        runs: s.TOTAL_RUNS,
        balls: s.BALLS_FACED,
        fours: s.FOURS,
        sixes: s.SIXES,
        strikeRate: s.STRIKE_RATE,
        dismissal: b.status === "OUT" ? formatDismissal(b.id, shared, appIndex) : "not out",
      };
    })
    // Only rows that batted at all — appearances without any batting stats
    // are DNB and shouldn't be listed.
    .filter((b) => b.runs != null || b.balls != null || b.status === "OUT" || b.status === "NOT_OUT");

  const bowlers = (bowl.appearances || [])
    .map((b) => {
      const s = statsMap(b.statistics);
      const p = resolvePlayer(b.id);
      return {
        appearanceId: b.id,
        playhqId: p.playhqId,
        name: p.displayName,
        overs: s.OVERS,
        maidens: s.MAIDENS,
        runs: s.RUNS,
        wickets: s.WICKETS,
        wides: s.WIDES,
        noBalls: s.NO_BALLS,
        economy: s.ECONOMY,
      };
    })
    .filter((b) => b.overs != null && b.overs !== 0);

  const totals = statsMap(bat.statistics);
  const fallOfWickets = (bat.fallOfWickets || []).map((f) => ({
    ...f,
    name: appearanceName(appIndex[f.appearanceId]),
  }));

  // ---- Fielding: aggregate catches / stumpings / run outs from the shared
  // dismissal records. PlayHQ doesn't expose per-appearance fielding
  // aggregates so we count them here from each caught / stumped / run-out
  // dismissal that has a FIELDING appearance recorded.
  const fielderMap = new Map();
  const bump = (appId, field) => {
    if (!appId) return;
    if (!fielderMap.has(appId)) {
      const p = resolvePlayer(appId);
      fielderMap.set(appId, {
        appearanceId: appId,
        playhqId: p.playhqId,
        name: p.displayName,
        catches: 0, stumpings: 0, runouts: 0,
      });
    }
    fielderMap.get(appId)[field] += 1;
  };
  (shared || []).forEach((d) => {
    const roles = (d.appearances || []).reduce((acc, a) => { acc[a.role] = a.id; return acc; }, {});
    if (!roles.FIELDING && !roles.ASSISTANT_FIELDING) return;
    switch (d.type) {
      case "CAUGHT": bump(roles.FIELDING, "catches"); break;
      case "STUMPED": bump(roles.FIELDING, "stumpings"); break;
      case "RUN_OUT":
        bump(roles.FIELDING, "runouts");
        bump(roles.ASSISTANT_FIELDING, "runouts");
        break;
      default: break;
    }
  });
  const fielders = Array.from(fielderMap.values())
    .filter((f) => (f.catches + f.stumpings + f.runouts) > 0)
    .sort((a, b) => (b.catches + b.stumpings + b.runouts) - (a.catches + a.stumpings + a.runouts) || a.name.localeCompare(b.name));

  return {
    battingTeamId: bat.id,
    bowlingTeamId: bowl.id,
    name: period.name,
    sequenceNo: period.sequenceNo,
    batters,
    bowlers,
    fielders,
    totals,
    fallOfWickets,
  };
}

/** Human-friendly one-line toss result: e.g. "Derwent CC 3 won the toss and elected to bowl". */
export function formatToss(coinToss, teamNameById) {
  if (!coinToss?.winningTeamId) return null;
  const name = teamNameById(coinToss.winningTeamId);
  const pref = coinToss.preference?.toLowerCase() || "bat";
  return `${name} won the toss and elected to ${pref}`;
}
